import React, { useState } from 'react';

import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import { FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ImageIcon from '@atlaskit/icon/core/image';
import { Popup } from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives/compiled';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import type { EditorExperimentsConfig } from '@atlaskit/tmp-editor-statsig/experiments-config';
import { token } from '@atlaskit/tokens';
import type { GenerateImageProps } from '@atlassian/ai-image-generation';

import type { CustomMediaConfig } from '../../../../controllers/use-product-dependencies';
import type { ImageProps } from '../../../layout-options';
import type { SelectedImage, UploadedImage } from '../../unsplash/types';

import { MediaPickerContent } from './content';
import { messages } from './messages';

const CC_PAGE_EXPERIENCES_PREMIUM_MACROS_IMG_GENERATION =
	'cc_page_experiences_premium_macros_img_generation' as keyof EditorExperimentsConfig;

const styles = cssMap({
	mediaPickerPopupContainer: {
		width: '264px',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
});

export type MediaPickerSectionProps = {
	analyticsSource: string;
	contentId: string;
	customMediaConfig?: CustomMediaConfig;
	generateAIHeaderImage?: (props: GenerateImageProps) => Promise<string | undefined>;
	image?: ImageProps;
	isAiHeaderImageLoading?: boolean;
	isDisabled?: boolean;
	onChangeImage: (image: UploadedImage | SelectedImage) => void;
	onOpenUpdate?: (open: boolean) => void;
	shouldAllowLinkFromUrl?: boolean;
	shouldShowDeleteButton?: boolean;
};

export const MediaPickerSection = ({
	onChangeImage,
	image,
	contentId,
	analyticsSource,
	shouldShowDeleteButton = false,
	onOpenUpdate,
	isDisabled = false,
	isAiHeaderImageLoading,
	customMediaConfig,
	shouldAllowLinkFromUrl = false,
	generateAIHeaderImage,
}: MediaPickerSectionProps): React.JSX.Element => {
	const [openPopup, setOpenPopup] = useState(false);
	const { formatMessage } = useIntl();

	const handleSetOpenPopup = (open: boolean) => {
		setOpenPopup(open);
		onOpenUpdate?.(open);
	};

	// When the Premium Extensions image generation experiment is enabled, the
	// media picker popup should always render above the trigger button. This
	// prevents the panel from being clipped by, or obscuring, content rendered
	// below the trigger in the Company Hub configuration UI.
	// Also gated behind the `confluence-ai` feature so that AI-related UI is
	// suppressed for IC (Isolated Cloud) tenants where Confluence AI is not
	// available.
	const isPremiumImgGenerationEnabled =
		expValEquals(CC_PAGE_EXPERIENCES_PREMIUM_MACROS_IMG_GENERATION, 'isEnabled', true) &&
		isFeatureEnabled('confluence', 'confluence-ai');
	const popupPlacement = isPremiumImgGenerationEnabled ? 'top-end' : 'bottom-end';

	return (
		<Popup
			isOpen={openPopup}
			onClose={() => handleSetOpenPopup(false)}
			content={() => (
				<Box xcss={styles.mediaPickerPopupContainer}>
					<MediaPickerContent
						elementIdPrefix={`${analyticsSource}-media-picker`}
						sectionLabel={<FormattedMessage {...messages.mediaPickerContentTitle} />}
						imageAltText={image?.altText}
						selectedImageUrl={image?.imageSrc}
						initialUnsplashResults={undefined}
						handleImageSelection={onChangeImage}
						contentId={contentId}
						analyticsSource={analyticsSource}
						helperMessage={<FormattedMessage {...messages.mediaPickerContentHelperMessage} />}
						shouldShowDeleteButton={shouldShowDeleteButton}
						isDisabled={isDisabled}
						customMediaConfig={customMediaConfig}
						shouldAllowLinkFromUrl={shouldAllowLinkFromUrl}
						generateAIHeaderImage={generateAIHeaderImage}
						isAiHeaderImageLoading={isAiHeaderImageLoading}
					/>
				</Box>
			)}
			placement={popupPlacement}
			trigger={(triggerProps) => (
				<Button
					{...triggerProps}
					analyticsContext={{ source: `${analyticsSource}MediaPickerButton` }}
					iconBefore={ImageIcon}
					iconAfter={(iconProps) => <ChevronDownIcon {...iconProps} size="small" />}
					onClick={() => handleSetOpenPopup(!openPopup)}
					aria-label={formatMessage(messages.mediaPickerButtonLabel)}
					isSelected={openPopup}
				>
					{/* Box added as button requires children */}
					<Box />
				</Button>
			)}
		/>
	);
};
